/**
 * Diagnostic Performance Measurement Utility
 * Phase 1-6 Analysis for lazy-loaded pages
 */
import * as Sentry from "@sentry/react";
import { safeMarkPerformance, safeMeasurePerformance, clearPerformanceMarks } from './performance-helpers';

export interface BundleMetrics {
  routeName: string;
  // Network timings
  downloadStart: number;
  downloadEnd: number;
  downloadDuration: number;
  
  // Parse/execution timings
  parseStart: number;
  parseEnd: number;
  parseDuration: number;
  
  // Component mount timings
  mountStart: number;
  mountEnd: number;
  mountDuration: number;
  
  // Total time
  totalDuration: number;
  
  // Resource analysis
  resources: ResourceMetric[];
  totalTransferSize: number;
  totalDecodedSize: number;
}

export interface ResourceMetric {
  name: string;
  size: number;
  transferSize: number;
  duration: number;
  type: string;
}

class DiagnosticPerformanceMonitor {
  private metrics: Map<string, BundleMetrics> = new Map();
  
  /**
   * Start measuring a lazy-loaded route
   */
  startRouteMeasure(routeName: string) {
    safeMarkPerformance(`${routeName}-download-start`);
    
    // Store initial state
    const metric: Partial<BundleMetrics> = {
      routeName,
      downloadStart: performance.now(),
      resources: [],
    };
    
    this.metrics.set(routeName, metric as BundleMetrics);
  }
  
  /**
   * Mark bundle download complete (before parse)
   */
  markBundleLoaded(routeName: string) {
    const metric = this.metrics.get(routeName);
    if (!metric) return;
    
    safeMarkPerformance(`${routeName}-download-end`);
    safeMarkPerformance(`${routeName}-parse-start`);
    
    metric.downloadEnd = performance.now();
    metric.downloadDuration = metric.downloadEnd - metric.downloadStart;
    metric.parseStart = metric.downloadEnd;
  }
  
  /**
   * Mark component mount start (after parse)
   */
  markMountStart(routeName: string) {
    const metric = this.metrics.get(routeName);
    if (!metric) return;
    
    safeMarkPerformance(`${routeName}-parse-end`);
    safeMarkPerformance(`${routeName}-mount-start`);
    
    metric.parseEnd = performance.now();
    metric.parseDuration = metric.parseEnd - metric.parseStart;
    metric.mountStart = metric.parseEnd;
  }
  
  /**
   * Mark component mount complete
   */
  markMountComplete(routeName: string) {
    const metric = this.metrics.get(routeName);
    if (!metric) return;
    
    safeMarkPerformance(`${routeName}-mount-end`);
    
    metric.mountEnd = performance.now();
    metric.mountDuration = metric.mountEnd - metric.mountStart;
    metric.totalDuration = metric.mountEnd - metric.downloadStart;
    
    // Analyze resources loaded for this route
    this.analyzeResources(routeName);
    
    // Create performance measures
    safeMeasurePerformance(
      `${routeName}-download`,
      `${routeName}-download-start`,
      `${routeName}-download-end`
    );
    safeMeasurePerformance(
      `${routeName}-parse`,
      `${routeName}-parse-start`,
      `${routeName}-parse-end`
    );
    safeMeasurePerformance(
      `${routeName}-mount`,
      `${routeName}-mount-start`,
      `${routeName}-mount-end`
    );
    
    // Clean up marks
    clearPerformanceMarks(
      `${routeName}-download-start`,
      `${routeName}-download-end`,
      `${routeName}-parse-start`,
      `${routeName}-parse-end`,
      `${routeName}-mount-start`,
      `${routeName}-mount-end`
    );
    
    // Log detailed results
    this.logResults(routeName);
  }
  
  /**
   * Analyze resources loaded during this route
   */
  private analyzeResources(routeName: string) {
    const metric = this.metrics.get(routeName);
    if (!metric) return;
    
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    // Filter resources loaded during this route's timeframe
    const routeResources = resources.filter(
      r => r.startTime >= metric.downloadStart && 
           r.startTime <= metric.mountEnd &&
           r.name.includes('.js')
    );
    
    let totalTransferSize = 0;
    let totalDecodedSize = 0;
    
    metric.resources = routeResources.map(r => {
      totalTransferSize += r.transferSize;
      totalDecodedSize += r.decodedBodySize;
      
      return {
        name: r.name.split('/').pop() || r.name,
        size: r.decodedBodySize,
        transferSize: r.transferSize,
        duration: r.duration,
        type: r.initiatorType
      };
    });
    
    metric.totalTransferSize = totalTransferSize;
    metric.totalDecodedSize = totalDecodedSize;
  }
  
  /**
   * Log comprehensive results
   */
  private logResults(routeName: string) {
    const metric = this.metrics.get(routeName);
    if (!metric) return;
    
    // Store in sessionStorage for analysis
    this.storeMetric(routeName, metric);
  }
  
  /**
   * Identify performance bottlenecks
   */
  private identifyBottlenecks(metric: BundleMetrics): string[] {
    const bottlenecks: string[] = [];
    
    // Check download time
    if (metric.downloadDuration > 300) {
      bottlenecks.push(`⚠️  Download time high (${metric.downloadDuration.toFixed(0)}ms) - Bundle too large or slow network`);
    }
    
    // Check parse time
    if (metric.parseDuration > 200) {
      bottlenecks.push(`⚠️  Parse/execution time high (${metric.parseDuration.toFixed(0)}ms) - Heavy JavaScript processing`);
    }
    
    // Check mount time
    if (metric.mountDuration > 150) {
      bottlenecks.push(`⚠️  Component mount slow (${metric.mountDuration.toFixed(0)}ms) - Expensive render or data fetching`);
    }
    
    // Check bundle size
    if (metric.totalDecodedSize > 150 * 1024) {
      bottlenecks.push(`⚠️  Bundle size exceeds 150KB (${(metric.totalDecodedSize / 1024).toFixed(0)}KB) - Consider code splitting`);
    }
    
    // Check number of resources
    if (metric.resources.length > 5) {
      bottlenecks.push(`⚠️  Multiple bundles loaded (${metric.resources.length}) - Possible waterfall loading`);
    }
    
    if (bottlenecks.length === 0) {
      bottlenecks.push('✅ No major bottlenecks detected');
    }
    
    return bottlenecks;
  }
  
  /**
   * Store metric for later analysis
   */
  private storeMetric(routeName: string, metric: BundleMetrics) {
    try {
      const stored = JSON.parse(sessionStorage.getItem('diagnostic-metrics') || '{}');
      stored[routeName] = metric;
      sessionStorage.setItem('diagnostic-metrics', JSON.stringify(stored));
    } catch (e) {
      Sentry.captureException(e, {
        tags: { context: 'performance_error', operation: 'diagnostic', action: 'store' },
        extra: { message: 'Failed to store diagnostic metrics', routeName }
      });
    }
  }
  
  /**
   * Get all stored metrics
   */
  getAllMetrics(): Record<string, BundleMetrics> {
    try {
      return JSON.parse(sessionStorage.getItem('diagnostic-metrics') || '{}');
    } catch {
      return {};
    }
  }
  
  /**
   * Generate comprehensive report
   */
  generateReport(): string {
    const allMetrics = this.getAllMetrics();
    const routes = Object.keys(allMetrics);
    
    if (routes.length === 0) {
      return 'No metrics collected yet. Navigate to lazy-loaded pages to collect data.';
    }
    
    let report = '# PHASE 1-6 DIAGNOSTIC REPORT\n\n';
    
    report += '## Bundle Size Breakdown\n\n';
    routes.forEach(route => {
      const m = allMetrics[route];
      report += `### ${route}\n`;
      report += `- Transfer Size: ${(m.totalTransferSize / 1024).toFixed(2)} KB\n`;
      report += `- Decoded Size: ${(m.totalDecodedSize / 1024).toFixed(2)} KB\n`;
      report += `- Resources: ${m.resources.length}\n`;
      report += `- Total Load Time: ${m.totalDuration.toFixed(0)}ms\n\n`;
    });
    
    report += '\n## Performance Breakdown by Phase\n\n';
    routes.forEach(route => {
      const m = allMetrics[route];
      report += `### ${route}\n`;
      report += `- Download: ${m.downloadDuration.toFixed(0)}ms (${((m.downloadDuration / m.totalDuration) * 100).toFixed(1)}%)\n`;
      report += `- Parse/Execute: ${m.parseDuration.toFixed(0)}ms (${((m.parseDuration / m.totalDuration) * 100).toFixed(1)}%)\n`;
      report += `- Mount: ${m.mountDuration.toFixed(0)}ms (${((m.mountDuration / m.totalDuration) * 100).toFixed(1)}%)\n\n`;
    });
    
    report += '\n## Bottleneck Analysis\n\n';
    routes.forEach(route => {
      const m = allMetrics[route];
      const bottlenecks = this.identifyBottlenecks(m);
      report += `### ${route}\n`;
      bottlenecks.forEach(b => report += `${b}\n`);
      report += '\n';
    });
    
    report += '\n## Resource Details\n\n';
    routes.forEach(route => {
      const m = allMetrics[route];
      report += `### ${route}\n`;
      m.resources.forEach(r => {
        report += `- ${r.name}: ${(r.size / 1024).toFixed(2)} KB (${r.duration.toFixed(0)}ms)\n`;
      });
      report += '\n';
    });
    
    return report;
  }
  
  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics.clear();
    sessionStorage.removeItem('diagnostic-metrics');
  }
}

// Export singleton instance
export const diagnosticMonitor = new DiagnosticPerformanceMonitor();

// Make available in browser console for manual testing
if (typeof window !== 'undefined') {
  (window as any).diagnosticMonitor = diagnosticMonitor;
}
